import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, Keyboard, Eye, MessageSquare } from "lucide-react";

export default function AccessibilityModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="accessibility-title"
          data-testid="accessibility-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0F1B2D] shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-[#1E293B] px-8 py-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#1E293B] bg-[#07111F] text-[#22C55E]">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-display text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
                    Statement
                  </p>
                  <h3
                    id="accessibility-title"
                    className="mt-1 font-display text-2xl font-bold tracking-tight text-[#F8FAFC] sm:text-[28px]"
                  >
                    Web Accessibility
                  </h3>
                  <p className="mt-1 font-body text-xs text-[#94A3B8]">
                    InfraYield's commitment to digital accessibility
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-md border border-[#1E293B] bg-[#07111F] p-2 text-[#94A3B8] transition-colors hover:border-[#38BDF8]/60 hover:text-[#F8FAFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38BDF8]/30"
                aria-label="Close accessibility statement"
                data-testid="accessibility-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[70vh] overflow-y-auto px-8 py-7">
              <p className="font-body text-[15px] leading-[1.75] text-[#CBD5E1]">
                InfraYield is committed to making our digital experiences accessible to
                the widest possible audience, including users with disabilities. We
                aim to comply with the{" "}
                <span className="font-semibold text-[#F8FAFC]">
                  Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
                </span>{" "}
                and applicable provisions of the Americans with Disabilities Act
                (ADA).
              </p>

              <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Pillar icon={Eye} title="Visual" note="Sufficient contrast and clear focus states." />
                <Pillar icon={Keyboard} title="Keyboard" note="Full navigation without a mouse." />
                <Pillar icon={MessageSquare} title="Semantic" note="Descriptive labels and landmark regions." />
              </div>

              <div className="mt-8 space-y-5">
                <Section title="Our Approach">
                  We build with semantic HTML, sufficient color contrast, keyboard
                  navigation, descriptive labels, and clearly visible focus states.
                  New features are reviewed for accessibility prior to release.
                </Section>

                <Section title="Ongoing Improvements">
                  Accessibility is an ongoing effort. We continually review and refine
                  our digital experiences and welcome feedback that helps us improve.
                </Section>

                <Section title="Reporting Issues">
                  If you encounter content or functionality that is not accessible to
                  you, please contact us with the page URL and a brief description of
                  the issue. We will respond promptly and work to provide the
                  information through an accessible alternative.
                </Section>

                <Section title="Contact">
                  Email{" "}
                  <a
                    href="mailto:accessibility@infrayield.co"
                    className="text-[#38BDF8] underline underline-offset-2 hover:text-[#F8FAFC]"
                  >
                    accessibility@infrayield.co
                  </a>{" "}
                  or use the contact form on our website.
                </Section>
              </div>

              <p className="mt-8 font-body text-xs text-[#64748B]">
                Last reviewed:{" "}
                {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}.
                © {new Date().getFullYear()} InfraYield. All rights reserved.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-[#1E293B] bg-[#07111F]/60 px-8 py-4">
              <a
                href="mailto:accessibility@infrayield.co"
                className="rounded-md border border-[#1E293B] bg-transparent px-5 py-2.5 font-body text-sm font-medium text-[#F8FAFC] transition-colors hover:border-[#38BDF8]/60 hover:bg-[#0F1B2D]"
              >
                Email accessibility team
              </a>
              <button
                onClick={onClose}
                className="rounded-md bg-[#22C55E] px-5 py-2.5 font-body text-sm font-semibold text-[#07111F] transition-colors hover:bg-[#22C55E]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
                data-testid="accessibility-acknowledge"
              >
                I understand
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-display text-sm font-semibold uppercase tracking-[0.12em] text-[#F8FAFC]">
        {title}
      </h4>
      <p className="mt-2 font-body text-[15px] leading-[1.75] text-[#CBD5E1]">
        {children}
      </p>
    </div>
  );
}

function Pillar({ icon: Icon, title, note }) {
  return (
    <div className="rounded-xl border border-[#1E293B] bg-[#07111F]/60 p-4">
      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-md border border-[#1E293B] bg-[#07111F] text-[#38BDF8]">
        <Icon className="h-4 w-4" />
      </div>
      <p className="font-display text-sm font-semibold text-[#F8FAFC]">{title}</p>
      <p className="mt-1 font-body text-xs text-[#94A3B8]">{note}</p>
    </div>
  );
}
